import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Link2, Mail, RotateCw, X } from 'lucide-react';

import { Button } from '@/components/ui/button';

import {
    ConfirmActionDialog,
    EmptyState,
    PillTabs,
    RelativeTime,
    SectionCard,
    StatusBadge,
    StatusTone,
    TablePagination,
} from '../../../../components/design-system/admin';
import { EmailError, Invitation, InvitationStatus, useInvitationsApi } from '../../../../common/hooks/use-invitations-api.hook';
import { readApiError } from '../../../../common/utils/api-error';

/** Where the list comes from: every invitation (super admin), or one vendor's (its team page). */
export interface InvitationsSource {
    listInvitations: (status: 'all') => Promise<Invitation[]>;
    resendInvitation: (id: string) => Promise<{ emailSent: boolean; emailError?: EmailError }>;
    revokeInvitation: (id: string) => Promise<unknown>;
}

type Filter = 'all' | InvitationStatus;

const PAGE_SIZES = [5, 10, 25];

const TONE: Record<InvitationStatus, StatusTone> = {
    pending: 'info',
    accepted: 'success',
    expired: 'neutral',
    revoked: 'danger',
};

/**
 * Every invitation, newest first, and what became of it: still pending,
 * accepted (and by whom), expired, or cancelled (and by whom). An email
 * invitation that is pending or expired can be sent again with a new link;
 * a pending one can be cancelled.
 */
export const Invitations = ({ showHeader = true, source }: { showHeader?: boolean; source?: InvitationsSource }) => {
    const { t } = useTranslation();
    const adminApi = useInvitationsApi();
    const invitationsApi: InvitationsSource = source || adminApi;

    const [invitations, setInvitations] = useState<Invitation[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [filter, setFilter] = useState<Filter>('all');
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
    const [busyId, setBusyId] = useState<string | null>(null);
    // Kept after closing so the dialog text doesn't blank out mid-animation.
    const [toRevoke, setToRevoke] = useState<Invitation | null>(null);
    const [revokeOpen, setRevokeOpen] = useState(false);

    const load = async () => {
        try {
            const list = await invitationsApi.listInvitations('all');
            setInvitations(Array.isArray(list) ? list : []);
            setLoadFailed(false);
        } catch (error) {
            console.error('Error loading invitations:', error);
            setLoadFailed(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const counts = useMemo(() => {
        const c: Record<Filter, number> = { all: invitations.length, pending: 0, accepted: 0, expired: 0, revoked: 0 };
        invitations.forEach((inv) => c[inv.status]++);
        return c;
    }, [invitations]);

    const shown = filter === 'all' ? invitations : invitations.filter((inv) => inv.status === filter);
    const lastPage = Math.max(0, Math.ceil(shown.length / pageSize) - 1);
    const currentPage = Math.min(page, lastPage);
    const rows = shown.slice(currentPage * pageSize, (currentPage + 1) * pageSize);

    const resend = async (invitation: Invitation) => {
        setBusyId(invitation._id);
        try {
            const { emailSent, emailError } = await invitationsApi.resendInvitation(invitation._id);
            if (emailSent) toast.success(t('ADMIN_USERS.INVITATIONS.RESENT', { email: invitation.email }));
            else if (emailError === 'not_configured') toast.error(t('ADMIN_USERS.INVITATIONS.MAIL_NOT_CONFIGURED'));
            else toast.error(t('ADMIN_USERS.INVITATIONS.RESEND_FAILED'));
            await load();
        } catch (error) {
            console.error('Error resending invitation:', error);
            const { code } = await readApiError(error);
            toast.error(t(code === 'NO_SEATS_LEFT' ? 'ADMIN_USERS.INVITATIONS.NO_SEATS_LEFT' : 'ADMIN_USERS.INVITATIONS.RESEND_FAILED'));
        } finally {
            setBusyId(null);
        }
    };

    const revoke = async () => {
        if (!toRevoke) return;
        setBusyId(toRevoke._id);
        try {
            await invitationsApi.revokeInvitation(toRevoke._id);
            toast.success(t('ADMIN_USERS.INVITATIONS.REVOKED'));
            await load();
        } catch (error) {
            console.error('Error cancelling invitation:', error);
            toast.error(t('ADMIN_USERS.INVITATIONS.REVOKE_FAILED'));
        } finally {
            setBusyId(null);
            setRevokeOpen(false);
        }
    };

    const outcome = (inv: Invitation) => {
        switch (inv.status) {
            case 'pending':
                return (
                    <>
                        {t('ADMIN_USERS.INVITATIONS.EXPIRES')} <RelativeTime value={inv.expiresAt} />
                    </>
                );
            case 'accepted':
                return inv.acceptedBy?.length
                    ? t('ADMIN_USERS.INVITATIONS.JOINED', { who: inv.acceptedBy.map((u) => u.name || u.email).join(', ') })
                    : null;
            case 'expired':
                return (
                    <>
                        {t('ADMIN_USERS.INVITATIONS.EXPIRED_ON')} <RelativeTime value={inv.expiresAt} />
                    </>
                );
            case 'revoked':
                return (
                    <>
                        {inv.revokedBy ? t('ADMIN_USERS.INVITATIONS.CANCELLED_BY', { who: inv.revokedBy }) : t('ADMIN_USERS.INVITATIONS.STATUS.REVOKED')}{' '}
                        <RelativeTime value={inv.revokedAt} />
                    </>
                );
        }
    };

    return (
        <SectionCard
            title={showHeader ? t('ADMIN_USERS.INVITATIONS.TITLE') : undefined}
            description={showHeader ? t('ADMIN_USERS.INVITATIONS.DESCRIPTION') : undefined}
            flush
            className={showHeader ? undefined : 'pt-4'}
            testId="invitations"
        >
            {loadFailed ? (
                <div className="flex items-center justify-between gap-3 px-6 py-4" data-testid="invitations-failed">
                    <p className="text-sm text-muted-foreground">{t('ADMIN_USERS.INVITATIONS.LOAD_FAILED')}</p>
                    <Button variant="outline" size="sm" onClick={load}>{t('ADMIN_SHELL.COMMON.RETRY')}</Button>
                </div>
            ) : !loading && invitations.length === 0 ? (
                <EmptyState
                    icon={<Mail />}
                    title={t('ADMIN_USERS.INVITATIONS.EMPTY_TITLE')}
                    description={t('ADMIN_USERS.INVITATIONS.EMPTY_DESCRIPTION')}
                    className="py-10"
                />
            ) : (
                <>
                    <div className="px-6 pb-3">
                        <PillTabs<Filter>
                            value={filter}
                            onChange={(value) => {
                                setFilter(value);
                                setPage(0);
                            }}
                            ariaLabel={t('ADMIN_USERS.INVITATIONS.FILTER_LABEL')}
                            options={(['all', 'pending', 'accepted', 'expired', 'revoked'] as Filter[]).map((value) => ({
                                value,
                                label: t(`ADMIN_USERS.INVITATIONS.FILTER.${value.toUpperCase()}`),
                                count: counts[value],
                            }))}
                        />
                    </div>

                    {rows.length === 0 ? (
                        <p className="border-t px-6 py-6 text-sm text-muted-foreground">{t('ADMIN_USERS.INVITATIONS.NO_MATCH')}</p>
                    ) : (
                        <ul className="divide-y border-t">
                            {rows.map((inv) => {
                                const canResend = inv.delivery === 'email' && (inv.status === 'pending' || inv.status === 'expired');
                                const canRevoke = inv.status === 'pending';
                                return (
                                    <li
                                        key={inv._id}
                                        className="flex flex-col gap-3 px-6 py-4 sm:flex-row sm:items-center sm:justify-between"
                                        data-testid="invitation-row"
                                        data-status={inv.status}
                                    >
                                        <div className="flex min-w-0 items-start gap-3">
                                            <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground [&_svg]:size-4">
                                                {inv.delivery === 'email' ? <Mail /> : <Link2 />}
                                            </span>
                                            <div className="flex min-w-0 flex-col gap-0.5">
                                                <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                                    <span className="truncate text-sm font-medium">
                                                        {inv.delivery === 'email' ? inv.email : t('ADMIN_USERS.INVITATIONS.LINK')}
                                                    </span>
                                                    <StatusBadge tone={TONE[inv.status]}>{t(`ADMIN_USERS.INVITATIONS.STATUS.${inv.status.toUpperCase()}`)}</StatusBadge>
                                                </span>
                                                <span className="text-xs text-muted-foreground">
                                                    {[
                                                        t(`ADMIN_USERS.ROLES.${inv.role}`),
                                                        inv.vendor?.name,
                                                        inv.delivery === 'link' && t('ADMIN_USERS.INVITATIONS.USES', { uses: inv.uses, max: inv.maxUses }),
                                                        t('ADMIN_USERS.INVITATIONS.INVITED_BY', { who: inv.invitedBy }),
                                                    ].filter(Boolean).join(' · ')}{' '}
                                                    · <RelativeTime value={inv.createdAt} />
                                                </span>
                                                <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                                                    <span data-testid="invitation-outcome">{outcome(inv)}</span>
                                                    {inv.status === 'pending' && inv.delivery === 'email' && !inv.lastSentAt && (
                                                        <StatusBadge tone="warning">{t('ADMIN_USERS.INVITATIONS.NOT_SENT')}</StatusBadge>
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                        {(canResend || canRevoke) && (
                                            <div className="flex shrink-0 gap-2" aria-label={t('ADMIN_USERS.INVITATIONS.ACTIONS')}>
                                                {canResend && (
                                                    <Button variant="outline" size="sm" onClick={() => resend(inv)} disabled={busyId === inv._id}>
                                                        <RotateCw />
                                                        {t('ADMIN_USERS.INVITATIONS.RESEND')}
                                                    </Button>
                                                )}
                                                {canRevoke && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => {
                                                            setToRevoke(inv);
                                                            setRevokeOpen(true);
                                                        }}
                                                        disabled={busyId === inv._id}
                                                    >
                                                        <X />
                                                        {t('ADMIN_USERS.INVITATIONS.REVOKE')}
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}

                    {shown.length > PAGE_SIZES[0] && (
                        <TablePagination
                            className="border-t"
                            page={currentPage}
                            pageSize={pageSize}
                            total={shown.length}
                            onPageChange={setPage}
                            onPageSizeChange={(size) => {
                                setPageSize(size);
                                setPage(0);
                            }}
                            pageSizes={PAGE_SIZES}
                        />
                    )}
                </>
            )}

            <ConfirmActionDialog
                open={revokeOpen}
                onOpenChange={setRevokeOpen}
                title={t('ADMIN_USERS.INVITATIONS.REVOKE_TITLE')}
                description={
                    toRevoke?.delivery === 'email'
                        ? t('ADMIN_USERS.INVITATIONS.REVOKE_DESCRIPTION_EMAIL', { email: toRevoke.email })
                        : t('ADMIN_USERS.INVITATIONS.REVOKE_DESCRIPTION_LINK')
                }
                confirmLabel={t('ADMIN_USERS.INVITATIONS.REVOKE_CONFIRM')}
                destructive
                busy={busyId === toRevoke?._id}
                onConfirm={revoke}
            />
        </SectionCard>
    );
};
